import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MagnifyingGlassIcon, SparklesIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { useAuth } from '../contexts/AuthContext';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { useWhatsNew } from '../hooks/useWhatsNew';
import { features, isNewFeature, Feature, FEATURE_GROUPS } from '../help/features';
import { releases } from '../help/releases';
import { helpModules } from '../help/modules';
import { canSeeFeature } from '../help/audience';

const RECENT_RELEASES = 3;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' });

const matches = (feature: Feature, query: string) =>
  `${feature.title} ${feature.description}`.toLocaleLowerCase('hu').includes(query.toLocaleLowerCase('hu'));

/**
 * Minden funkció egy helyen, linkkel az appon belüli helyére, és az újdonságok.
 * A felhasználó jogosultságai és a klub bekapcsolt moduljai szerint szűr.
 */
const Features: React.FC = () => {
  usePageTitle('Funkciók és újdonságok');
  const { user } = useAuth();
  const { settings } = useClubSettings();
  const { markSeen } = useWhatsNew();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [showAllReleases, setShowAllReleases] = useState(false);

  // Aki megnyitotta az oldalt, az látta az újdonságokat
  useEffect(() => {
    markSeen();
  }, [markSeen]);

  const visible = useMemo(() => features.filter((f) => canSeeFeature(user, f, settings)), [user, settings]);
  const visibleIds = useMemo(() => new Set(visible.map((f) => f.id)), [visible]);
  const byId = useMemo(() => new Map(features.map((f) => [f.id, f])), []);

  const shown = query.trim() ? visible.filter((f) => matches(f, query.trim())) : visible;
  const newCount = visible.filter((f) => isNewFeature(f)).length;
  const moduleCount = new Set(visible.map((f) => f.module)).size;

  // Az újdonságokból csak az látszik, ami a felhasználónak szól
  const relevantReleases = releases
    .map((r) => ({ ...r, items: r.items.filter((i) => !i.feature || visibleIds.has(i.feature)) }))
    .filter((r) => r.items.length > 0);
  const shownReleases = showAllReleases ? relevantReleases : relevantReleases.slice(0, RECENT_RELEASES);

  // /features#funkcio link a kártyához görget
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id) document.getElementById(id)?.scrollIntoView({ block: 'center' });
  }, [location.hash]);

  const moduleTitle = (id: string) => helpModules.find((m) => m.id === id)?.title ?? id;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Funkciók és újdonságok</h1>
        <p className="text-sm text-gray-500">
          {visible.length} funkció {moduleCount} modulban
          {newCount > 0 && <> · ebből {newCount} új az elmúlt 30 napban</>}
        </p>
      </div>

      {/* Újdonságok */}
      <section aria-labelledby="whats-new" className="mb-8 rounded-xl border border-primary-200 bg-primary-50/70 p-4 sm:p-5">
        <h2 id="whats-new" className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <SparklesIcon className="h-5 w-5 text-primary-600" />
          Újdonságok
        </h2>
        <ol className="mt-3 space-y-4">
          {shownReleases.map((release) => (
            <li key={`${release.date}-${release.title}`} className="rounded-lg bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <h3 className="font-semibold text-gray-900">{release.title}</h3>
                <time dateTime={release.date} className="text-xs text-gray-500">
                  {formatDate(release.date)}
                </time>
              </div>
              <ul className="mt-2 space-y-1.5 text-sm text-gray-700">
                {release.items.map((item) => {
                  const feature = item.feature ? byId.get(item.feature) : undefined;
                  return (
                    <li key={item.text} className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between sm:gap-3">
                      <span>{item.text}</span>
                      {feature && (
                        <Link
                          to={feature.to}
                          className="shrink-0 text-sm font-medium text-primary-700 hover:text-primary-800"
                        >
                          Kipróbálom →
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ol>
        {relevantReleases.length > RECENT_RELEASES && (
          <button
            type="button"
            onClick={() => setShowAllReleases((v) => !v)}
            className="mt-3 text-sm font-medium text-primary-700 hover:text-primary-800"
          >
            {showAllReleases ? 'Kevesebb' : `Korábbi újdonságok (${relevantReleases.length - RECENT_RELEASES})`}
          </button>
        )}
      </section>

      {/* Funkciók */}
      <div className="relative mb-6">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Keresés a funkciók között, pl. Excel, felállás, értesítés..."
          aria-label="Keresés a funkciók között"
          className="block w-full rounded-lg border-gray-300 pl-10 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
        />
      </div>

      {shown.length === 0 && (
        <p className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Nincs találat.</p>
      )}

      <div className="space-y-8">
        {FEATURE_GROUPS.map((group) => {
          const items = shown.filter((f) => group.modules.includes(f.module));
          if (items.length === 0) return null;
          return (
            <section key={group.title} aria-labelledby={`group-${group.title}`}>
              <h2 id={`group-${group.title}`} className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {group.title}
              </h2>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((f) => (
                  <article
                    key={f.id}
                    id={f.id}
                    className={`flex scroll-mt-24 flex-col rounded-xl bg-white p-4 shadow-sm ring-1 ${
                      location.hash === `#${f.id}` ? 'ring-2 ring-primary-500' : 'ring-gray-100'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-gray-900">{f.title}</h3>
                      {isNewFeature(f) && (
                        <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Új
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500">{moduleTitle(f.module)}</p>
                    <p className="mt-2 flex-1 text-sm text-gray-700">{f.description}</p>
                    <div className="mt-3 flex items-center justify-between gap-3 text-sm">
                      <Link
                        to={f.to}
                        className="inline-flex items-center gap-1 font-medium text-primary-700 hover:text-primary-800"
                      >
                        Megnyitás <ArrowRightIcon className="h-4 w-4" />
                      </Link>
                      <Link to={`/help#${f.module}`} className="whitespace-nowrap text-gray-500 hover:text-gray-700">
                        Hogyan működik?
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default Features;
